import {
  getTassoQuintoErrorCode,
  getTassoQuintoErrorDetails,
  getTassoQuintoErrorStatus,
} from '../../../services/api/tasso-quinto';
import { ACQUISITION_INCOMPLETE_REASON } from './acquisitionOutcome';

const SESSION_EXPIRED_MESSAGE =
  'La sessione è scaduta. Ricarica la pagina e ripeti l’acquisizione.';

const INVALID_REQUEST_MESSAGE =
  'La richiesta non è stata accettata: controlla i dati inseriti e riprova.';

const IDEMPOTENCY_CONFLICT_MESSAGE =
  'I dati sono stati modificati dopo un primo invio. Ricompila la richiesta da capo per acquisirla.';

const IDEMPOTENCY_IN_PROGRESS_MESSAGE =
  'L’acquisizione è già in corso. Attendi qualche istante prima di riprovare.';

export const ACQUISITION_OPERATION_GONE_MESSAGE =
  'L’operazione non è più disponibile. Se la lead non risulta acquisita, ricompila la richiesta da capo.';

const READINESS_BLOCKED_MESSAGE =
  'L’acquisizione non è disponibile per una configurazione mancante su HubSpot. Segnala il problema: non dipende dai dati inseriti.';

/**
 * The refusals that read the same whatever product was being acquired. The
 * workflow codes reach the browser as an `incomplete` outcome far more often
 * than as a refusal, but when one does arrive as an error it says the same
 * thing, so this starts from those explanations rather than restating them.
 */
const MESSAGE_BY_CODE: Record<string, string> = {
  ...ACQUISITION_INCOMPLETE_REASON,
  invalid_token: SESSION_EXPIRED_MESSAGE,
  invalid_request: INVALID_REQUEST_MESSAGE,
  idempotency_key_required: INVALID_REQUEST_MESSAGE,
  invalid_idempotency_key: INVALID_REQUEST_MESSAGE,
  idempotency_conflict: IDEMPOTENCY_CONFLICT_MESSAGE,
  idempotency_in_progress: IDEMPOTENCY_IN_PROGRESS_MESSAGE,
  hubspot_readiness_blocked: READINESS_BLOCKED_MESSAGE,
  acquisition_operation_not_found: ACQUISITION_OPERATION_GONE_MESSAGE,
};

/**
 * The refusals that are the acquisition working as designed: a profile the
 * policy does not accept, a worker already inside the lease, a key reused after
 * an edit, an operation past retention, a session to renew, a scope to be
 * granted. Each is explained above and each has a way forward the operator can
 * take, so none of them is news to anyone watching the system.
 *
 * Deliberately absent: the schema and key refusals. `invalid_request` and the
 * two `Idempotency-Key` codes say the browser built something the closed
 * contract does not accept, which the operator can neither cause nor correct.
 *
 * They are named here, beside the vocabulary that explains them, so what is
 * worth reporting and what is worth saying stay one decision.
 */
const EXPECTED_REFUSAL_CODES = new Set([
  'ineligible_profile',
  'idempotency_conflict',
  'idempotency_in_progress',
  'acquisition_operation_not_found',
  'insufficient_scope',
  'invalid_token',
]);

/**
 * Whether this refusal is one of those. A `401` or a `403` that names no code
 * we know is still a session or a permission rather than a defect, so the
 * status decides where the code cannot.
 */
export const isExpectedAcquisitionRefusal = (error: unknown) => {
  const code = getTassoQuintoErrorCode(error);
  if (code && EXPECTED_REFUSAL_CODES.has(code)) return true;

  const status = getTassoQuintoErrorStatus(error);
  return status === 401 || status === 403;
};

interface AcquisitionFailureVocabulary {
  /** How the product is named in a sentence: `una lead ${leadLabel}`. */
  leadLabel: string;
  /** The product's mirror of the shared eligibility policy's reasons. */
  ineligibilityMessages: Record<string, string>;
  /** What an ineligible profile reads as when its reason is unknown here. */
  ineligibleFallback: string;
  /** What a failure the codes and statuses cannot classify reads as. */
  genericFailure: string;
}

/**
 * Builds the translator from a refused acquisition to what the operator reads.
 * The stable `code` decides, never the backend's `message`, which is
 * explanatory prose the contract does not promise to keep. A response that
 * names no code we know is still classified by its status where the status
 * alone is unambiguous, so a missing permission never reads as a data mistake.
 *
 * A `422 ineligible_profile` is explained with the same sentence the product's
 * inline check would have produced, so the operator never reads two different
 * accounts of one rule — and a rejection that reaches this point is a defect in
 * the client's mirror of the policy, not a request to try again.
 */
export const acquisitionFailureDescriber = ({
  leadLabel,
  ineligibilityMessages,
  ineligibleFallback,
  genericFailure,
}: AcquisitionFailureVocabulary) => {
  const permissionDenied = `Non sei autorizzato ad acquisire una lead ${leadLabel}. Chiedi che ti venga assegnato il permesso di acquisizione.`;

  const describeIneligibleProfile = (error: unknown) => {
    const reason = getTassoQuintoErrorDetails(error).reason;
    return typeof reason === 'string' &&
      Object.prototype.hasOwnProperty.call(ineligibilityMessages, reason)
      ? ineligibilityMessages[reason]
      : ineligibleFallback;
  };

  return (error: unknown): string => {
    const code = getTassoQuintoErrorCode(error);
    if (code === 'ineligible_profile') return describeIneligibleProfile(error);
    if (code === 'insufficient_scope') return permissionDenied;
    if (code && MESSAGE_BY_CODE[code]) return MESSAGE_BY_CODE[code];

    const status = getTassoQuintoErrorStatus(error);
    if (status === 403) return permissionDenied;
    if (status === 401) return SESSION_EXPIRED_MESSAGE;

    return genericFailure;
  };
};
